import { useEffect, useState } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { fetchTrends, type Trends as TrendsData } from '../lib/api'
import { Card, SectionTitle, EmptyState } from '../components/ui'
import { assignCategoryColors, CHART } from '../lib/palette'
import { metalLabel, aud } from '../lib/format'

export default function Trends() {
  const [data, setData] = useState<TrendsData | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { fetchTrends().then(setData).catch((e) => setError(String(e))) }, [])

  if (error) return <EmptyState title="Couldn't load trends" hint={error} />
  if (!data) return <p className="font-body text-sm text-ink/40">Loading…</p>

  const hasHistory = data.price_series.length > 1
  const colors = assignCategoryColors(data.metals)

  return (
    <div className="space-y-6">
      <SectionTitle note="median price per metal over time, overlaid with spot">Price trends</SectionTitle>
      <Card className="p-4">
        {hasHistory ? (
          <ResponsiveContainer width="100%" height={380}>
            <LineChart data={data.price_series} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid stroke={CHART.grid} />
              <XAxis dataKey="d" tick={{ fill: CHART.muted, fontSize: 12 }} stroke={CHART.axis} />
              <YAxis tick={{ fill: CHART.muted, fontSize: 12 }} stroke={CHART.axis}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={48} />
              <Tooltip formatter={(v: number, name: string) => [aud(v, { round: true }), metalLabel(name)]} />
              <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => metalLabel(v)} />
              {data.metals.map((m) => (
                <Line key={m} type="monotone" dataKey={m} stroke={colors.get(m)} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState
            title="Trend history builds over weekly cycles"
            hint="Each scrape cycle appends a price_history row when a price changes. After a couple of cycles this chart separates 'market repricing to gold' from 'a competitor changed strategy' — with the spot line overlaid."
          />
        )}
      </Card>

      <SectionTitle note="pure-metal spot snapshots (AUD/gram)">Spot price history</SectionTitle>
      <Card className="p-4">
        {data.spot_series.length > 1 ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.spot_series} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid stroke={CHART.grid} />
              <XAxis dataKey="d" tick={{ fill: CHART.muted, fontSize: 12 }} stroke={CHART.axis} />
              <YAxis tick={{ fill: CHART.muted, fontSize: 12 }} stroke={CHART.axis} width={44} />
              <Tooltip formatter={(v: number, n: string) => [`$${v}/g`, n]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="gold" stroke={CHART.gold} strokeWidth={2} dot={false} connectNulls />
              <Line type="monotone" dataKey="platinum" stroke="#4a3aa7" strokeWidth={2} dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState title="One spot snapshot so far" hint="Spot is captured each cycle (Admin → refresh spot). The line grows from there." />
        )}
      </Card>
    </div>
  )
}
